import { useEffect, useRef, useState } from 'react';

import { ArrowRightIcon, CircleIcon } from '@/assets/icons';
import { Badge, SvgIcon } from '@/components/common';

interface IDashedArrow {
  duration?: string;
  showDuration?: boolean;
}

export const DashedArrow = ({ duration, showDuration = false }: IDashedArrow) => {
  const badgeRef = useRef<HTMLSpanElement>(null);
  const [badgeWidth, setBadgeWidth] = useState(0);

  useEffect(() => {
    const element = badgeRef.current;
    if (!showDuration || !duration || !element) return;

    const update = () => setBadgeWidth(element.offsetWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [duration, showDuration]);

  // When showDuration is true, leave a gap in the middle for the badge.
  // When showDuration is false, the two lines are equal (50% each) and meet in the middle.
  const lineWidth = showDuration
    ? badgeWidth > 0
      ? `calc(50% - ${badgeWidth / 2 + 4}px)`
      : 'calc(50% - 1rem)'
    : 'calc(50% - 1px)';

  return (
    <div className="w-full flex items-center justify-center">
      <SvgIcon component={CircleIcon} fill="currentColor" className="size-2 text-neutral-7" />
      <div className="relative flex-1 self-stretch flex items-center justify-center">
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 border-t border-dashed border-neutral-7"
          style={{ width: lineWidth }}
        />
        {showDuration && (
          <Badge ref={badgeRef} border mode="normal" variant="neutral-4" size="sm" className="z-10">
            {duration ? (
              duration
            ) : (
              <span className="tracking-[1px] font-semibold text-body-xs">--</span>
            )}
          </Badge>
        )}
        <span
          className="absolute right-0 top-1/2 -translate-y-1/2 border-t border-dashed border-neutral-7"
          style={{ width: lineWidth }}
        />
      </div>
      <SvgIcon component={ArrowRightIcon} className="size-3 text-neutral-7" />
    </div>
  );
};
